import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { StopOutlined } from "@ant-design/icons";
import Button from "../components/Button";
import SEO from "../components/SEO";
import { APP_NAME, APP_SUPPORT_EMAIL } from "../helpers/constants";

export default function PurchaseCancelled() {
  return (
    <>
      <SEO
        title={`Checkout cancelled · ${APP_NAME}`}
        description="Your checkout was not completed. You can return to pricing and try again, or contact support for help."
        noindex
      />
      <PageShell>
        <Card>
          <Eyebrow>Plan checkout</Eyebrow>
          <Stamp aria-hidden>
            <StopOutlined />
          </Stamp>
          <Title>Payment not completed</Title>
          <Lead>
            No charges were made. You can close this tab or continue from the
            links below whenever you are ready.
          </Lead>
          <ReasonPanel>
            <ReasonPanelLabel>Common reasons</ReasonPanelLabel>
            <ReasonList>
              <li>You left Stripe before confirming payment</li>
              <li>Your bank or card issuer declined the attempt</li>
              <li>The checkout session timed out</li>
            </ReasonList>
          </ReasonPanel>
          <FootNote>
            Questions?{" "}
            <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a>
            {" · "}
            <Link to="/contact">Contact</Link>
          </FootNote>
          <Actions>
            <TryAgainButton
              as={Link}
              to="/#pricing"
              variant="primary"
              rounded="none"
              width="auto"
            >
              Return to pricing
            </TryAgainButton>
            <HomeLink to="/">Back to home</HomeLink>
          </Actions>
        </Card>
      </PageShell>
    </>
  );
}

const PageShell = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  box-sizing: border-box;
  background: var(--color-primary-dark, #0f172a);
  background-image: repeating-linear-gradient(
    -14deg,
    transparent,
    transparent 10px,
    rgba(255, 255, 255, 0.03) 10px,
    rgba(255, 255, 255, 0.03) 20px
  );
`;

const Card = styled.main`
  width: 100%;
  max-width: 34rem;
  margin: 0 auto;
  padding: 2.25rem 1.75rem 2rem;
  background: var(--color-bg-primary, #fff);
  border: var(--border-brutal, 3px) solid var(--color-border, #1e293b);
  box-shadow: var(--shadow-dark, 5px 5px 0 0 #1e293b);
  text-align: center;

  @media (min-width: 480px) {
    padding: 2.5rem 2.25rem;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 1rem;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-action, #ff5722);
`;

const Stamp = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.75rem;
  height: 3.75rem;
  margin: 0 auto 1.25rem;
  font-size: 1.65rem;
  color: var(--color-action, #ff5722);
  border: var(--border-brutal, 3px) solid var(--color-action, #ff5722);
  box-shadow: var(--shadow-light, 2px 2px 0 0 #1e293b);
  background: var(--color-action-glow, rgba(255, 87, 34, 0.08));
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  font-size: clamp(1.35rem, 4vw, 1.65rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary, #1e293b);
`;

const Lead = styled.p`
  margin: 0;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--color-text-secondary, #374151);
`;

const ReasonPanel = styled.div`
  margin: 1.5rem 0 1.25rem;
  padding: 1.1rem 1.1rem 1.1rem 1.15rem;
  text-align: left;
  background: var(--color-bg-tertiary, #f8fafc);
  border: 2px solid var(--color-border-light, #e2e8f0);
  border-left: 4px solid var(--color-action, #ff5722);
  box-shadow: var(--shadow-light, 2px 2px 0 0 #1e293b);
`;

const ReasonPanelLabel = styled.span`
  display: block;
  margin-bottom: 0.6rem;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-muted, #64748b);
`;

const ReasonList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--color-text-secondary, #374151);

  li + li {
    margin-top: 0.4rem;
  }
`;

const FootNote = styled.p`
  margin: 0 0 1.5rem;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--color-text-muted, #64748b);

  a {
    color: var(--color-text-primary, #1e293b);
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.75rem 1rem;
`;

const TryAgainButton = styled(Button)`
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 48px !important;
  min-height: 48px !important;
  padding: 0 1.35rem !important;
  line-height: 1 !important;
  border: none !important;
  box-shadow: var(--shadow-medium, 3px 3px 0 0 #1e293b) !important;
  font-weight: 800 !important;
  font-size: 0.9375rem !important;
  text-decoration: none !important;
  color: #ffffff !important;
  background-color: var(--color-action, #ff5722) !important;

  &:visited,
  &:hover,
  &:active {
    color: #ffffff !important;
    text-decoration: none !important;
  }

  &:hover:not(:disabled) {
    background-color: var(--color-action-hover, #f44e1b) !important;
    box-shadow: var(--shadow-dark, 5px 5px 0 0 #1e293b) !important;
  }

  &:focus-visible {
    outline: 2px solid var(--color-action, #ff5722);
    outline-offset: 2px;
  }
`;

const HomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 0.5rem;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-primary, #1e293b);
  text-decoration: underline;
  text-underline-offset: 3px;
  border: none;
  background: transparent;
  box-shadow: none;
  transition: color 0.15s ease;

  &:hover {
    color: var(--color-action, #ff5722);
  }

  &:focus-visible {
    outline: 2px solid var(--color-action, #ff5722);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;
